import React, { useState, useEffect } from 'react';
import {
  adminLogin,
  getAdminToken,
  clearAdminToken,
  listBots,
  createBot,
  updateBot,
  deleteBot,
  botStats,
  botTrades,
} from '../services/adminApi';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import {
  Loader2,
  LogOut,
  Plus,
  Trash2,
  RefreshCw,
  X,
  ExternalLink,
} from 'lucide-react';

const emptyForm = {
  name: '',
  pair_address: '',
  token0_address: '',
  token1_address: '',
  token0_symbol: '',
  token1_symbol: '',
  token0_decimals: 18,
  token1_decimals: 18,
  private_key: '',
  peg_price: 1,
  deviation_tolerance: 2,
  max_trade_usd: 50,
  daily_budget_usd: 2000,
  interval_seconds: 30,
  organic_volume_enabled: true,
  organic_min_usd: 5,
  organic_max_usd: 20,
  organic_min_interval_seconds: 300,
  organic_max_interval_seconds: 900,
  enabled: false,
};

const AdminPage = () => {
  const [authed, setAuthed] = useState(!!getAdminToken());
  const [password, setPassword] = useState('');
  const [loggingIn, setLoggingIn] = useState(false);
  const [bots, setBots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [statsByBot, setStatsByBot] = useState({});
  const [openTrades, setOpenTrades] = useState(null); // botId
  const [tradesList, setTradesList] = useState([]);

  const refresh = async () => {
    setLoading(true);
    try {
      const list = await listBots();
      setBots(list);
      const statEntries = await Promise.all(
        list.map(async (b) => {
          try {
            return [b.id, await botStats(b.id)];
          } catch {
            return [b.id, null];
          }
        })
      );
      setStatsByBot(Object.fromEntries(statEntries));
    } catch (err) {
      console.error(err);
      if (err.response?.status === 401) {
        clearAdminToken();
        setAuthed(false);
      }
      toast.error('Failed to load bots');
    }
    setLoading(false);
  };

  useEffect(() => {
    if (authed) refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authed]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoggingIn(true);
    try {
      await adminLogin(password);
      setAuthed(true);
      setPassword('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Login failed');
    }
    setLoggingIn(false);
  };

  const handleLogout = () => {
    clearAdminToken();
    setAuthed(false);
    setBots([]);
  };

  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await createBot({
        ...form,
        peg_price: parseFloat(form.peg_price),
        deviation_tolerance: parseFloat(form.deviation_tolerance),
        max_trade_usd: parseFloat(form.max_trade_usd),
        daily_budget_usd: parseFloat(form.daily_budget_usd),
        interval_seconds: parseInt(form.interval_seconds),
        organic_min_usd: parseFloat(form.organic_min_usd),
        organic_max_usd: parseFloat(form.organic_max_usd),
        organic_min_interval_seconds: parseInt(form.organic_min_interval_seconds),
        organic_max_interval_seconds: parseInt(form.organic_max_interval_seconds),
        token0_decimals: parseInt(form.token0_decimals),
        token1_decimals: parseInt(form.token1_decimals),
      });
      toast.success('Bot config created');
      setShowForm(false);
      setForm(emptyForm);
      await refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create');
    }
    setSaving(false);
  };

  const handleToggle = async (bot) => {
    try {
      await updateBot(bot.id, { enabled: !bot.enabled });
      toast.success(`Bot ${!bot.enabled ? 'enabled' : 'disabled'}`);
      await refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to toggle');
    }
  };

  const handleDelete = async (bot) => {
    if (!window.confirm(`Delete bot config "${bot.name}"? Trade history will also be deleted.`))
      return;
    try {
      await deleteBot(bot.id);
      toast.success('Deleted');
      await refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleViewTrades = async (botId) => {
    setOpenTrades(botId);
    try {
      const trades = await botTrades(botId, 100);
      setTradesList(trades);
    } catch (err) {
      toast.error('Failed to load trades');
    }
  };

  // ---- LOGIN VIEW ----
  if (!authed) {
    return (
      <div className="min-h-[calc(100vh-64px)] bg-[#0d0d0d] flex items-center justify-center px-4">
        <Card className="bg-[#1a1a1a] border-white/5 p-8 rounded-2xl w-full max-w-md">
          <h1 className="text-2xl font-bold text-white mb-1">Admin Panel</h1>
          <p className="text-gray-500 text-sm mb-6">BlokSwap operator console</p>
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <Label className="text-gray-400">Admin password</Label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="bg-white/5 border-white/10 text-white"
                data-testid="admin-password-input"
                required
              />
            </div>
            <Button
              type="submit"
              disabled={loggingIn}
              data-testid="admin-login-btn"
              className="w-full bg-yellow-500 text-black hover:bg-yellow-400"
            >
              {loggingIn ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Sign in'}
            </Button>
          </form>
        </Card>
      </div>
    );
  }

  // ---- DASHBOARD VIEW ----
  return (
    <div className="min-h-[calc(100vh-64px)] bg-[#0d0d0d] text-white">
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold">Market-Maker Bots</h1>
            <p className="text-gray-500 text-sm mt-1">Manage automated trading per pair</p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="ghost"
              onClick={refresh}
              data-testid="admin-refresh-btn"
              className="text-gray-300 hover:text-white"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            <Button
              onClick={() => setShowForm(true)}
              data-testid="admin-add-bot-btn"
              className="bg-yellow-500 text-black hover:bg-yellow-400"
            >
              <Plus className="w-4 h-4 mr-2" />
              New bot
            </Button>
            <Button
              variant="ghost"
              onClick={handleLogout}
              data-testid="admin-logout-btn"
              className="text-gray-400 hover:text-white"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {loading && (
          <div className="flex justify-center py-10">
            <Loader2 className="w-6 h-6 animate-spin text-yellow-400" />
          </div>
        )}

        {!loading && bots.length === 0 && (
          <Card className="bg-[#1a1a1a] border-white/5 p-12 rounded-2xl text-center">
            <p className="text-gray-400 mb-4">No bot configurations yet.</p>
            <Button
              onClick={() => setShowForm(true)}
              className="bg-yellow-500 text-black hover:bg-yellow-400"
            >
              Create your first bot
            </Button>
          </Card>
        )}

        <div className="grid gap-4">
          {bots.map((b) => {
            const stats = statsByBot[b.id];
            return (
              <Card
                key={b.id}
                className="bg-[#1a1a1a] border-white/5 p-6 rounded-2xl"
                data-testid={`bot-card-${b.id}`}
              >
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-xl font-semibold">{b.name}</h3>
                      <span className="text-xs px-2 py-0.5 rounded-full bg-white/5 text-gray-400">
                        {b.token0_symbol}/{b.token1_symbol}
                      </span>
                      <span
                        className={`text-xs px-2 py-0.5 rounded-full ${
                          b.enabled
                            ? 'bg-green-500/20 text-green-400'
                            : 'bg-gray-500/20 text-gray-400'
                        }`}
                      >
                        {b.enabled ? 'Running' : 'Stopped'}
                      </span>
                    </div>
                    <div className="text-sm text-gray-400 grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
                      <div>
                        <div className="text-gray-500 text-xs">Peg</div>
                        <div className="text-white">{b.peg_price}</div>
                      </div>
                      <div>
                        <div className="text-gray-500 text-xs">Tolerance</div>
                        <div className="text-white">±{b.deviation_tolerance}%</div>
                      </div>
                      <div>
                        <div className="text-gray-500 text-xs">Max trade</div>
                        <div className="text-white">${b.max_trade_usd}</div>
                      </div>
                      <div>
                        <div className="text-gray-500 text-xs">Daily budget</div>
                        <div className="text-white">${b.daily_budget_usd}</div>
                      </div>
                    </div>
                    <div className="text-xs text-gray-500 break-all">
                      Bot wallet:{' '}
                      <a
                        href={`https://bccscan.com/address/${b.bot_wallet_address}`}
                        target="_blank"
                        rel="noreferrer"
                        className="text-yellow-400 hover:underline"
                      >
                        {b.bot_wallet_address}
                      </a>
                    </div>
                    <div className="text-xs text-gray-500 break-all">
                      Pair: {b.pair_address}
                    </div>
                    {stats && (
                      <div className="mt-3 text-sm text-gray-400 flex gap-4">
                        <span>
                          24h trades:{' '}
                          <b className="text-white">{stats.trades_24h}</b>
                        </span>
                        <span>
                          24h vol:{' '}
                          <b className="text-white">${stats.volume_24h_usd}</b>
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col gap-2 sm:flex-row md:flex-col items-start md:items-end">
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={b.enabled}
                        onCheckedChange={() => handleToggle(b)}
                        data-testid={`toggle-${b.id}`}
                      />
                      <span className="text-sm text-gray-400">
                        {b.enabled ? 'On' : 'Off'}
                      </span>
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleViewTrades(b.id)}
                      data-testid={`view-trades-${b.id}`}
                      className="text-gray-300"
                    >
                      Trades
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDelete(b)}
                      data-testid={`delete-${b.id}`}
                      className="text-red-400 hover:text-red-300"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      </div>

      {/* ---- CREATE FORM MODAL ---- */}
      {showForm && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur z-50 flex items-center justify-center p-4">
          <Card className="bg-[#1a1a1a] border-white/10 p-6 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-white">New bot configuration</h2>
              <button
                onClick={() => setShowForm(false)}
                className="text-gray-400 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Name" v={form.name} onChange={(v) => setF('name', v)} testid="form-name" full />
              <Field label="Pair address (LP)" v={form.pair_address} onChange={(v) => setF('pair_address', v)} testid="form-pair" full />
              <Field label="Token0 address" v={form.token0_address} onChange={(v) => setF('token0_address', v)} testid="form-t0" />
              <Field label="Token1 address" v={form.token1_address} onChange={(v) => setF('token1_address', v)} testid="form-t1" />
              <Field label="Token0 symbol" v={form.token0_symbol} onChange={(v) => setF('token0_symbol', v)} testid="form-t0-sym" />
              <Field label="Token1 symbol" v={form.token1_symbol} onChange={(v) => setF('token1_symbol', v)} testid="form-t1-sym" />
              <Field label="Token0 decimals" v={form.token0_decimals} onChange={(v) => setF('token0_decimals', v)} type="number" />
              <Field label="Token1 decimals" v={form.token1_decimals} onChange={(v) => setF('token1_decimals', v)} type="number" />
              <Field
                label="Bot wallet private key (encrypted on save)"
                v={form.private_key}
                onChange={(v) => setF('private_key', v)}
                type="password"
                testid="form-pk"
                full
              />
              <Field label="Peg price (token1/token0)" v={form.peg_price} onChange={(v) => setF('peg_price', v)} type="number" testid="form-peg" />
              <Field label="Deviation tolerance %" v={form.deviation_tolerance} onChange={(v) => setF('deviation_tolerance', v)} type="number" testid="form-tol" />
              <Field label="Max trade USD" v={form.max_trade_usd} onChange={(v) => setF('max_trade_usd', v)} type="number" />
              <Field label="Daily budget USD" v={form.daily_budget_usd} onChange={(v) => setF('daily_budget_usd', v)} type="number" />
              <Field label="Cycle interval (s)" v={form.interval_seconds} onChange={(v) => setF('interval_seconds', v)} type="number" />
              <div className="col-span-2 border-t border-white/5 pt-3 mt-2">
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-gray-300">Organic volume swaps</Label>
                  <Switch
                    checked={form.organic_volume_enabled}
                    onCheckedChange={(v) => setF('organic_volume_enabled', v)}
                  />
                </div>
              </div>
              <Field label="Organic min USD" v={form.organic_min_usd} onChange={(v) => setF('organic_min_usd', v)} type="number" />
              <Field label="Organic max USD" v={form.organic_max_usd} onChange={(v) => setF('organic_max_usd', v)} type="number" />
              <Field label="Organic min interval (s)" v={form.organic_min_interval_seconds} onChange={(v) => setF('organic_min_interval_seconds', v)} type="number" />
              <Field label="Organic max interval (s)" v={form.organic_max_interval_seconds} onChange={(v) => setF('organic_max_interval_seconds', v)} type="number" />
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <Button variant="ghost" onClick={() => setShowForm(false)} className="text-gray-300">
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                disabled={saving}
                data-testid="form-save-btn"
                className="bg-yellow-500 text-black hover:bg-yellow-400"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create bot'}
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* ---- TRADES MODAL ---- */}
      {openTrades && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur z-50 flex items-center justify-center p-4">
          <Card className="bg-[#1a1a1a] border-white/10 p-6 rounded-2xl w-full max-w-3xl max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-white">Recent bot trades</h2>
              <button
                onClick={() => {
                  setOpenTrades(null);
                  setTradesList([]);
                }}
                className="text-gray-400 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            {tradesList.length === 0 ? (
              <p className="text-gray-500 py-6 text-center">No trades yet.</p>
            ) : (
              <table className="w-full text-sm">
                <thead className="text-gray-500">
                  <tr className="text-left">
                    <th className="py-2">Time</th>
                    <th className="py-2">Kind</th>
                    <th className="py-2">Direction</th>
                    <th className="py-2 text-right">USD</th>
                    <th className="py-2">Status</th>
                    <th className="py-2">Tx</th>
                  </tr>
                </thead>
                <tbody className="text-white">
                  {tradesList.map((t) => (
                    <tr key={t.id} className="border-t border-white/5">
                      <td className="py-2 text-gray-400">
                        {new Date(t.timestamp).toLocaleString()}
                      </td>
                      <td className="py-2">{t.kind}</td>
                      <td className="py-2">{t.direction}</td>
                      <td className="py-2 text-right">${t.trade_usd?.toFixed(2) || '0'}</td>
                      <td className={`py-2 ${t.status === 'confirmed' ? 'text-green-400' : 'text-red-400'}`}>
                        {t.status}
                      </td>
                      <td className="py-2">
                        {t.tx_hash && (
                          <a
                            href={`https://bccscan.com/tx/${t.tx_hash}`}
                            target="_blank"
                            rel="noreferrer"
                            className="text-yellow-400 hover:underline inline-flex items-center gap-1"
                          >
                            view <ExternalLink className="w-3 h-3" />
                          </a>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Card>
        </div>
      )}
    </div>
  );
};

const Field = ({ label, v, onChange, type = 'text', testid, full }) => (
  <div className={full ? 'col-span-2' : ''}>
    <Label className="text-gray-400 text-xs mb-1 block">{label}</Label>
    <Input
      type={type}
      value={v}
      onChange={(e) => onChange(e.target.value)}
      className="bg-white/5 border-white/10 text-white"
      data-testid={testid}
    />
  </div>
);

export default AdminPage;
